import { ICON_COLOR } from '@zep-us/zus/tokens';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { useViewport } from '@/hooks';
import { CheckSmallCircleIcon } from '@/icons';
import { cn } from '@/utils';
import { useBuildFormContext } from '../form/schema';
import { BuildHeaderMapTemplateButton } from './BuildHeaderMapTemplateButton';
import { BuildHeaderNextButton } from './BuildHeaderNextButton';
import { BuildHeaderSubmitButton } from './BuildHeaderSubmitButton';
import { BuildHeaderTitleInput } from './BuildHeaderTitleInput';
import { BuildStepToggleTab } from './BuildStepToggleTab';
import { ContinueOnPersistBuildButton } from './ContinueOnPersistBuildButton';
import { RouteBackButton } from './RouteBackButton';
import { UserChip } from './UserChip';
export type BuildStep = 'quiz' | 'settings';
type BuildHeaderProps = {
  step: BuildStep;
  onStepChange: (step: BuildStep) => void;
  onSubmitQuiz: () => void;
  isSubmitButtonShown: boolean;
  isBuildPageBodyInView: boolean;
};
export const BuildHeader = ({
  step,
  onStepChange,
  isSubmitButtonShown,
  onSubmitQuiz,
  isBuildPageBodyInView
}: BuildHeaderProps) => {
  const {
    getValues
  } = useBuildFormContext();
  const {
    isPortable
  } = useViewport();
  const {
    t
  } = useTranslation(['build', 'common']);
  return <div className={'fixed left-0 right-0 top-0 z-50  h-auto bg-white transition-all duration-200 ease-in-out'} data-sentry-component="BuildHeader" data-sentry-source-file="BuildHeader.tsx">
      {/* Header container */}
      <div className="flex flex-col">
        {/* Main Header */}
        <div className={cn('flex flex-wrap items-center justify-between gap-x-sm px-md border-b border-alternative', 'tablet-lg:grid tablet-lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] tablet-lg:gap-sm tablet-lg:h-[64px]')}>
          {/* Left section */}
          <div className="flex h-[64px] min-w-0 flex-1 items-center gap-xs overflow-hidden">
            <RouteBackButton data-sentry-element="RouteBackButton" data-sentry-source-file="BuildHeader.tsx" />
            <BuildHeaderMapTemplateButton data-sentry-element="BuildHeaderMapTemplateButton" data-sentry-source-file="BuildHeader.tsx" />
            <BuildHeaderTitleInput data-sentry-element="BuildHeaderTitleInput" data-sentry-source-file="BuildHeader.tsx" />
            <UserChip data-sentry-element="UserChip" data-sentry-source-file="BuildHeader.tsx" />
          </div>

          <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="BuildHeader.tsx">
            <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: !isPortable || isBuildPageBodyInView ? '62px' : 0,
            opacity: !isPortable || isBuildPageBodyInView ? 1 : 0,
            willChange: 'height, opacity'
          }} transition={{
            duration: 0.2,
            ease: 'easeInOut'
          }} className={cn('w-full flex items-center justify-center pb-sm pt-2xs order-3', 'tablet-lg:w-auto tablet-lg:py-0 tablet-lg:order-2 tablet-lg:border-0', isPortable && !isBuildPageBodyInView && 'p-0')} data-sentry-element="unknown" data-sentry-source-file="BuildHeader.tsx">
              <BuildStepToggleTab currentStep={step} onBuildStepChange={onStepChange} data-sentry-element="BuildStepToggleTab" data-sentry-source-file="BuildHeader.tsx" />
            </motion.div>
          </AnimatePresence>

          {/* Right section */}
          <div className="order-2 flex h-[64px] items-center gap-xs tablet-lg:order-3 tablet-lg:flex-1 tablet-lg:justify-end">
            <div className={'mr-xs hidden shrink-0 items-center text-[13px] font-semibold leading-normal text-[#9E9FAF] mobile-lg:flex'}>
              <CheckSmallCircleIcon size={'xs'} fill={ICON_COLOR.positive} className="mr-2xs" data-sentry-element="CheckSmallCircleIcon" data-sentry-source-file="BuildHeader.tsx" />
              {t('common:saving')}
              <div className="ml-sm h-[18px] w-[1px] border-l border-l-alternative" />
            </div>
            <ContinueOnPersistBuildButton data-sentry-element="ContinueOnPersistBuildButton" data-sentry-source-file="BuildHeader.tsx" />
            {isSubmitButtonShown ? <BuildHeaderSubmitButton onSubmitQuiz={onSubmitQuiz} /> : <BuildHeaderNextButton onClick={() => {
            logger.track('click_next_step_button', {
              ...getValues(),
              quizList: getValues('quizList')?.length || 0,
              materials: getValues('materials')?.length || 0
            });
            onStepChange('settings');
          }} />}
          </div>
        </div>
      </div>
    </div>;
};